import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import WritingSettings, { GearIcon } from './WritingSettings';

const API = process.env.REACT_APP_BACKEND_URL;

// A series: its chapters, a look at the story bible, and "write the next
// ones". Chapters are written one after another on the server, each one
// reading the bible and the chapters before it. The gear has the writer and
// editor models, the full bible, and delete.

const ChevronIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const num = (n) => Number(n || 0).toLocaleString();
const SECTION_NAMES = { characters: 'characters', world: 'places & things', rules: 'rules', timeline: "what's happened", style: 'style & voice' };
const TrashIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 7h16" /><path d="M10 11v6M14 11v6" /><path d="M6 7l1 13h10l1-13" /><path d="M9 7V4h6v3" />
  </svg>
);

export default function WritingBook() {
  const navigate = useNavigate();
  const { bookId } = useParams();
  const token = localStorage.getItem('token');
  const [work, setWork] = useState(null);
  const [count, setCount] = useState(1);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const load = () => {
    axios.get(`${API}/writing/works/${bookId}?token=${token}`)
      .then(res => { setWork(res.data.work); setErrorMsg(''); })
      .catch(err => setErrorMsg(err?.response?.data?.detail || "couldn't load this series"));
  };
  useEffect(() => {
    if (!token) { navigate('/'); return undefined; }
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [bookId]);

  const act = (key, req, after) => {
    setBusy(key);
    req.then(res => { after && after(res); load(); })
      .catch(err => setErrorMsg(err?.response?.data?.detail || 'that didn\'t work — try again'))
      .finally(() => setBusy(''));
  };

  if (!work) {
    return (
      <div style={s.page}>
        <header style={s.header}><button style={s.back} onClick={() => navigate('/writing')} aria-label="back">←</button></header>
        <main style={s.main}>{errorMsg ? <div style={s.error}>{errorMsg}</div> : <div style={s.muted}>opening…</div>}</main>
      </div>
    );
  }

  const chapters = work.chapters || [];
  const nextUp = chapters.find(c => c.status === 'planned');
  const firstNew = nextUp ? nextUp.n : chapters.length + 1;
  const anyRunning = chapters.some(c => c.status === 'writing' || c.status === 'queued');
  const open = (c) => { if (c.status === 'written' || c.status === 'writing') navigate(`/writing/p/${c.id}`); };

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <style>{`
        .wb-row { transition: background .15s ease; }
        .wb-row.can:hover { background: rgba(201,147,90,0.05); }
        .wb-btn:focus-visible, .wb-row:focus-visible { outline: 2px solid #c9935a; outline-offset: 2px; }
        .wb-in:focus { border-color: rgba(201,147,90,0.5) !important; }
        .wb-pulse { animation: wbpulse 1.6s ease-in-out infinite; }
        @keyframes wbpulse { 0%,100% { opacity: .45; } 50% { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .wb-pulse { animation: none; } }
      `}</style>

      <header style={s.header}>
        <button className="wb-btn" style={s.back} onClick={() => navigate('/writing')} aria-label="back">←</button>
        <div style={s.title}>series</div>
        <button className="wb-btn" style={s.gear} onClick={() => setSettingsOpen(true)} aria-label="settings" title="settings"><GearIcon /></button>
      </header>

      <main style={s.main}>
        <div>
          <div style={s.heroTitle}>{work.title}</div>
          <div style={s.heroSub}>
            {[work.world, `${work.written} of ${work.chapters_planned} chapters`, `${num(work.words)} words`, `about ${num(work.target_words)} a chapter`].filter(Boolean).join(' · ')}
          </div>
          {work.arc && <div style={s.arc}>{work.arc}</div>}
        </div>

        {errorMsg && <div style={s.error} onClick={() => setErrorMsg('')}>{errorMsg}</div>}

        <section style={s.card}>
          <div style={s.cardLabel}>chapters</div>
          <div>
            {chapters.map(c => {
              const can = c.status === 'written' || c.status === 'writing';
              return (
                <div key={c.id} className={`wb-row${can ? ' can' : ''}`} style={s.row(can)} role={can ? 'button' : undefined} tabIndex={can ? 0 : undefined}
                  onClick={() => open(c)} onKeyDown={e => e.key === 'Enter' && open(c)}>
                  <span style={s.chNum(c.status)}>{c.n}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={s.chTitle(c.status)}>{/^Chapter \d+$/.test(c.title) ? (c.status === 'planned' || c.status === 'queued' ? 'not written yet' : c.title) : c.title}</div>
                    {c.status === 'written' && <div style={s.chSub}>{num(c.words)} words</div>}
                    {c.status === 'writing' && c.activity && (
                      <>
                        <div style={s.chLive}><span className="wb-pulse" style={s.liveDot} />{c.activity.label}</div>
                        <div style={s.bar}><div style={{ ...s.fill, width: `${Math.round((c.activity.progress || 0) * 100)}%` }} /></div>
                      </>
                    )}
                    {c.status === 'queued' && <div style={s.chSub}>up next — starts when the one before is done</div>}
                    {c.status === 'planned' && c.outline && <div style={s.chSub}>{c.outline}</div>}
                    {c.status === 'error' && <div style={{ ...s.chSub, color: '#c98a7a' }}>didn't finish — it's tried again next time you write the next chapter</div>}
                  </div>
                  {can && <span style={s.chev}><ChevronIcon /></span>}
                </div>
              );
            })}
          </div>

          <div style={s.nextBox}>
            <div style={s.nextLabel}>write the next</div>
            <div style={s.chipRow}>
              {[1, 3, 5].map(n => (
                <button key={n} className="wb-btn" style={s.chip(count === n)} onClick={() => setCount(n)}>
                  {n === 1 ? '1 chapter' : `${n} chapters`}
                </button>
              ))}
            </div>
            <button className="wb-btn" style={busy ? s.goOff : s.go} disabled={!!busy}
              onClick={() => act('next', axios.post(`${API}/writing/works/${work.id}/next`, { token, count }))}>
              {count === 1 ? `write chapter ${firstNew}` : `write chapters ${firstNew}–${firstNew + count - 1}`}
            </button>
            <div style={s.hint}>
              {anyRunning
                ? 'they go in a queue after the one being written. each one reads the bible and every chapter before it.'
                : 'each chapter reads the bible and every chapter before it, then the editor checks it like the others.'}
            </div>
          </div>
        </section>

        <section style={s.card} role="button" tabIndex={0} className="wb-row can"
          onClick={() => navigate(`/writing/bible/${work.id}`)} onKeyDown={e => e.key === 'Enter' && navigate(`/writing/bible/${work.id}`)}>
          <div style={s.bibleHead}>
            <div>
              <div style={s.cardLabel}>story bible</div>
              <div style={s.bibleSub}>{work.bible_count} things the writer and editor keep true</div>
            </div>
            <span style={s.chev}><ChevronIcon /></span>
          </div>
          {work.suggestions > 0 && (
            <div style={s.waiting}>{work.suggestions} new thing{work.suggestions === 1 ? '' : 's'} the writer noticed, waiting for you</div>
          )}
          <div style={s.bibleGrid}>
            {(work.bible_preview || []).filter(b => b.count).map(b => (
              <div key={b.section} style={s.bibleCell}>
                <div style={s.bibleName}>{SECTION_NAMES[b.section] || b.section} <span style={s.bibleN}>{b.count}</span></div>
                {b.subjects.length > 0 && <div style={s.bibleSubjects}>{b.subjects.slice(0, 4).join(' · ')}{b.subjects.length > 4 ? ' …' : ''}</div>}
              </div>
            ))}
          </div>
        </section>
      </main>
      {settingsOpen && (
        <WritingSettings work={work} onClose={() => setSettingsOpen(false)} onSaved={load}
          onOpenBible={() => navigate(`/writing/bible/${work.id}`)}>
          {!confirmDelete
            ? <button className="wb-btn" style={s.sheetRow} onClick={() => setConfirmDelete(true)}><TrashIcon /> delete this series</button>
            : (
              <div style={s.confirm}>
                <div>delete “{work.title}” and all {work.written} chapters? this can't be undone.</div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <button className="wb-btn" style={s.danger} onClick={() => act('del', axios.delete(`${API}/writing/works/${work.id}?token=${token}`), () => navigate('/writing'))}>delete</button>
                  <button className="wb-btn" style={s.quiet} onClick={() => setConfirmDelete(false)}>keep it</button>
                </div>
              </div>
            )}
        </WritingSettings>
      )}
    </div>
  );
}

const s = {
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0e0b0f', color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 },
  header: { position: 'sticky', top: 0, zIndex: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '12px', background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)' },
  back: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  title: { flex: 1, fontFamily: "'Cormorant Garamond', serif", fontSize: '21px', fontStyle: 'italic' },
  gear: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  bibleHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' },
  bibleSub: { fontSize: '13px', color: '#c9b8a6', marginTop: '4px' },
  waiting: { fontSize: '12.5px', color: '#c9935a', padding: '8px 12px', borderRadius: '12px', background: 'rgba(201,147,90,0.08)', border: '1px solid rgba(201,147,90,0.22)' },
  bibleGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '8px' },
  bibleCell: { padding: '10px 12px', borderRadius: '12px', background: '#17131b' },
  bibleName: { fontSize: '12.5px', color: '#e0d4c6' },
  bibleN: { color: '#6d5d4e', marginLeft: '4px' },
  bibleSubjects: { fontSize: '11.5px', color: '#8a7466', marginTop: '3px', lineHeight: 1.45 },
  sheetRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', borderRadius: '16px', border: 'none', background: '#17131b', color: '#c98a7a', fontSize: '14px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  main: { maxWidth: '720px', margin: '0 auto', padding: '26px 20px 90px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '16px' },
  muted: { color: '#8a7466', fontSize: '13px' },
  heroTitle: { fontFamily: "'Cormorant Garamond', serif", fontSize: '34px', fontStyle: 'italic', lineHeight: 1.1 },
  heroSub: { fontSize: '12.5px', color: '#8a7466', marginTop: '8px' },
  arc: { fontFamily: "'Cormorant Garamond', serif", fontSize: '17px', lineHeight: 1.55, color: '#c9b8a6', marginTop: '12px', paddingLeft: '14px', borderLeft: '2px solid rgba(201,147,90,0.3)' },
  card: { background: '#1e1825', border: '1px solid rgba(201,147,90,0.08)', borderRadius: '20px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '10px' },
  cardLabel: { fontSize: '10px', color: '#8a7466', letterSpacing: '0.12em', textTransform: 'uppercase' },
  row: (can) => ({ display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '12px 8px', borderRadius: '12px', cursor: can ? 'pointer' : 'default', borderTop: '1px solid rgba(201,147,90,0.06)' }),
  chNum: (st) => ({ width: '26px', textAlign: 'center', fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', lineHeight: 1, color: st === 'written' || st === 'writing' ? '#c9935a' : '#4d4038', flexShrink: 0, paddingTop: '2px' }),
  chTitle: (st) => ({ fontFamily: "'Cormorant Garamond', serif", fontSize: '19px', lineHeight: 1.25, color: st === 'written' || st === 'writing' ? '#e8ddd0' : '#6d5d4e', fontStyle: st === 'planned' || st === 'queued' ? 'italic' : 'normal' }),
  chSub: { fontSize: '12px', color: '#7a6a5a', marginTop: '3px', lineHeight: 1.5 },
  chLive: { display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', color: '#c9a57a', marginTop: '4px' },
  liveDot: { width: '7px', height: '7px', borderRadius: '50%', background: '#c9935a', flexShrink: 0 },
  bar: { height: '4px', borderRadius: '2px', background: 'rgba(201,147,90,0.12)', overflow: 'hidden', marginTop: '8px' },
  fill: { height: '100%', background: 'linear-gradient(90deg, #8a5f35, #c9935a)', transition: 'width .6s ease' },
  chev: { color: '#6d5d4e', paddingTop: '4px' },
  nextBox: { marginTop: '6px', padding: '16px', borderRadius: '16px', background: '#17131b', display: 'flex', flexDirection: 'column', gap: '10px' },
  nextLabel: { fontFamily: "'Cormorant Garamond', serif", fontSize: '19px', fontStyle: 'italic' },
  chipRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  chip: (on) => ({ padding: '7px 14px', borderRadius: '16px', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", border: on ? 'none' : '1px solid rgba(201,147,90,0.18)', background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent', color: on ? '#0e0b0f' : '#c9a57a' }),
  go: { padding: '13px', borderRadius: '14px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '14px', fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  goOff: { padding: '13px', borderRadius: '14px', border: 'none', background: 'rgba(201,147,90,0.15)', color: 'rgba(201,147,90,0.45)', fontSize: '14px', fontFamily: "'DM Sans', sans-serif" },
  hint: { fontSize: '12px', color: '#7a6a5a', lineHeight: 1.6 },
  confirm: { padding: '14px', borderRadius: '14px', background: 'rgba(180,60,60,0.08)', border: '1px solid rgba(220,100,100,0.2)', fontSize: '13px', color: '#e0c0b0', lineHeight: 1.5 },
  danger: { padding: '8px 16px', borderRadius: '12px', border: 'none', background: '#a0473a', color: '#fff', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  quiet: { padding: '8px 16px', borderRadius: '12px', border: '1px solid rgba(201,147,90,0.2)', background: 'transparent', color: '#c9a57a', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  error: { background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0', cursor: 'pointer' },
};
